import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import styles from "./modal.module.scss";
import { Props } from "./types";
import { ModalSizes, ModalDisplay } from "./model";
import { IoMdClose, IoMdHelp } from "react-icons/io";
import { BiWindows, BiWindow } from "react-icons/bi";
import styleMerger from "@pga/utils/styleMerger/styleMerger";

export const Modal = forwardRef(
  (
    {
      componentId,
      children,
      title,
      defaultSize = ModalSizes.lg,
      icon,
      hasClose,
      hasResize,
      helpBtn,
      disableOutsideClosing,
      scrollable,
      helpClick,
      includingHeader = true,
      customStyles,
      isBlurFilter,
      closeIcon,
      afterClose,
      bgColor,
      width,
      height,
    }: Props,
    ref
  ) => {
    const [size, setSize] = useState<ModalSizes>(ModalSizes.lg);
    const [display, setDisplay] = useState<ModalDisplay>(ModalDisplay.hide);
    const modalContainerRef = useRef<HTMLDivElement>(null);
    const modalRef = useRef<HTMLDivElement>(null);
    const [mergedStyle, setMergedStyles] = useState(styles);

    useEffect(() => {
      setSize(defaultSize);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const open = () => {
      setDisplay(ModalDisplay.show);
    };

    const close = () => {
      setDisplay(ModalDisplay.hide);
      afterClose && afterClose();
    };

    const onClickCloseHandler = (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!modalRef.current?.contains(target)) {
        setDisplay(ModalDisplay.hide);
        afterClose && afterClose();
      }
    };

    const resizeHandler = () => {
      setSize((prev) =>
        prev === ModalSizes.md ? ModalSizes.lg : ModalSizes.md
      );
    };

    useImperativeHandle(ref, () => ({
      open,
      close,
      resizeHandler,
    }));
    useEffect(() => {
      customStyles && styleMerger(styles, customStyles!, setMergedStyles);
    }, [customStyles]);
    const modalSize = () => {
      switch (size) {
        case ModalSizes.md:
          return styles.md;
        case ModalSizes.lg:
          return styles.lg;
        case ModalSizes.sm:
          return styles.sm;
        case ModalSizes.xs:
          return styles.xs;
      }
    };
    return (
      <div
        id={componentId}
        className={`
          ${display === ModalDisplay.show ? "" : styles.closed}
          ${mergedStyle["_container"]}
          ${disableOutsideClosing && styles["disableOutsideClosing"]}
          `}
        ref={modalContainerRef}
        onClick={onClickCloseHandler}
        style={
          isBlurFilter
            ? {
                backgroundColor: "#939DA9BF",
                backdropFilter: "blur(5px)",
              }
            : {}
        }
      >
        <div
          className={`${mergedStyle["_main"]} 
            ${width === undefined && height === undefined && modalSize()}
            `}
          ref={modalRef}
          style={{
            backgroundColor: bgColor !== null ? bgColor : "#939DA9BF",
            width: width && width,
            height: height && height,
          }}
        >
          {includingHeader && (
            <div className={mergedStyle["_main-header"]}>
              <div className={mergedStyle["_main-header-title"]}>
                {icon}
                {title}
              </div>
              <div className={mergedStyle["_main-header-buttons"]}>
                {helpBtn && (
                  <div
                    className={`${mergedStyle["_main-header-buttons-act"]}`}
                    onClick={helpClick}
                  >
                    <IoMdHelp style={{ marginInlineEnd: "5px" }} size={25} />
                  </div>
                )}
                {hasResize && (
                  <div
                    className={`${mergedStyle["_main-header-buttons-act"]}`}
                    onClick={resizeHandler}
                  >
                    {size === ModalSizes.lg ? (
                      <BiWindows size={25} />
                    ) : (
                      <BiWindow size={25} />
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          {hasClose && !includingHeader && (
            <div
              className={`${mergedStyle["_buttons-close"]} ${styles.closer}`}
              onClick={() => setDisplay(ModalDisplay.hide)}
            >
              {closeIcon ? closeIcon : <IoMdClose size={30} />}
            </div>
          )}
          <div
            className={`
            ${styles["_main-content"]}
            ${scrollable ? styles.scrollable : ""}
            `}
          >
            {children}
          </div>
        </div>
      </div>
    );
  }
);
